<template>
  <div
    v-if="alerts.length > 0"
    class="alert-banner-list"
  >
    <div
      v-for="alert in alerts"
      :key="alert.id"
      class="alert-banner"
      :class="`alert-banner--${alert.severity}`"
    >
      <div class="alert-banner__icon">
        <i :class="iconFor(alert.severity)" />
      </div>
      <div class="alert-banner__body">
        <p class="alert-banner__message">
          {{ alert.message }}
        </p>
      </div>
      <Button
        v-if="alert.cta"
        :label="alert.cta.label"
        class="p-button-sm alert-banner__cta"
        @click="$emit('cta', alert)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useStore } from '@/store';
import Button from 'primevue/button';
import { AlertSeverity, BillingAlert } from '@/types/billing-types';

export default defineComponent({
  name: 'AlertBanner',

  components: { Button },

  emits: ['cta'],

  setup() {
    const store = useStore();
    const alerts = computed<BillingAlert[]>(() => (
      store.state.billing.overview?.alerts ?? []
    ));

    const iconFor = (s: AlertSeverity) => {
      if (s === 'danger') return 'fa-regular fa-circle-exclamation';
      if (s === 'warning') return 'fa-regular fa-triangle-exclamation';
      return 'fa-regular fa-circle-info';
    };

    return { alerts, iconFor };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.alert-banner-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.alert-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid transparent;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
    font-size: 1rem;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__message {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #1f2937;
  }

  &__cta {
    flex-shrink: 0;
    font-weight: 600 !important;
  }

  &--info {
    background: #f6faff;
    border-color: #dbeafe;

    .alert-banner__icon {
      background: #dbeafe;
      color: #1d4ed8;
    }
  }

  &--warning {
    background: #fffaf0;
    border-color: #fef3c7;

    .alert-banner__icon {
      background: #fef3c7;
      color: #b45309;
    }
    .alert-banner__cta {
      background: #fab710 !important;
      border-color: #fab710 !important;
      color: #fff !important;
    }
  }

  &--danger {
    background: #fef6f6;
    border-color: #fee2e2;

    .alert-banner__icon {
      background: #fee2e2;
      color: #b91c1c;
    }
    .alert-banner__cta {
      background: #ef4444 !important;
      border-color: #ef4444 !important;
      color: #fff !important;
    }
  }
}
</style>
